import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, LineChart, Line,
} from 'recharts';
import { useReportsSummary, useExportTickets, useExportTasks } from '../hooks/useReports';
import Button from '../components/Button';
import AnalyticsCards from '../components/AnalyticsCards';

const COLORS = ['#26148C', '#5B21FF', '#A7A1FF', '#86E9FF', '#FF31B0', '#010E23', '#1A7A3C'];

export default function Reports() {
  const { summary, loading }          = useReportsSummary();
  const { exportTickets, exporting: exportingTickets } = useExportTickets();
  const { exportTasks,   exporting: exportingTasks }   = useExportTasks();

  const byStatus   = summary ? Object.entries(summary.byStatus   || {}).map(([name, value]) => ({ name, value })) : [];
  const byPriority = summary ? Object.entries(summary.byPriority || {}).map(([name, value]) => ({ name, value })) : [];
  const byCategory = summary ? Object.entries(summary.byCategory || {}).map(([name, value]) => ({ name, value })) : [];
  const trend      = summary?.trend || [];

  const anyExporting = exportingTickets || exportingTasks;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-lg font-bold text-[#010E23]">Reports</h1>

        <div className="flex flex-wrap gap-2">
          {/* Ticket exports */}
          <div className="flex gap-1.5 items-center">
            <span className="text-xs text-gray-400 mr-1">Tickets:</span>
            {(['excel', 'csv', 'pdf'] as const).map(fmt => (
              <Button key={fmt} variant="ghost" size="sm"
                disabled={anyExporting}
                onClick={() => exportTickets(fmt)}>
                {exportingTickets ? 'Exporting…' : fmt.toUpperCase()}
              </Button>
            ))}
          </div>

          {/* Task exports */}
          <div className="flex gap-1.5 items-center">
            <span className="text-xs text-gray-400 mr-1">Tasks:</span>
            {(['excel', 'csv'] as const).map(fmt => (
              <Button key={fmt} variant="ghost" size="sm"
                disabled={anyExporting}
                onClick={() => exportTasks(fmt)}>
                {exportingTasks ? 'Exporting…' : fmt.toUpperCase()}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Analytics: SLA attainment, CSAT, agent leaderboard */}
      <AnalyticsCards />

      {/* KPI cards */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-4">
          <div className="w-4 h-4 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          Loading…
        </div>
      ) : summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total',        value: summary.total },
            { label: 'Completed',    value: summary.completed },
            { label: 'SLA Breached', value: summary.slaBreached, danger: true },
            { label: 'Pending',      value: summary.pending },
          ].map(c => (
            <div
              key={c.label}
              className={`rounded-xl p-4 text-white ${c.danger ? 'bg-red-700' : 'bg-[#010E23]'}`}
            >
              <p className="text-2xl font-bold">{c.value}</p>
              <p className="text-xs text-white/60 mt-0.5">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Tickets by Status">
          <PieChart>
            <Pie data={byStatus} dataKey="value" nameKey="name"
              cx="50%" cy="50%" outerRadius={80}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
              {byStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ChartCard>

        <ChartCard title="Tickets by Priority">
          <BarChart data={byPriority} margin={{ left: -20 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {byPriority.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="Top Categories">
          <BarChart data={byCategory.slice(0, 6)} layout="vertical" margin={{ left: 20 }}>
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={130} />
            <Tooltip />
            <Bar dataKey="value" fill="#26148C" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ChartCard>

        <ChartCard title="Monthly Trend">
          <LineChart data={trend} margin={{ left: -20 }}>
            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#26148C" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ChartCard>
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <h2 className="text-sm font-semibold text-[#010E23] mb-4">{title}</h2>
      <ResponsiveContainer width="100%" height={220}>
        {children as any}
      </ResponsiveContainer>
    </div>
  );
}
